import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Star, Trash2 } from "lucide-react";

const STATUS_TABS = [
    { id: "pending",  label: "Pending" },
    { id: "approved", label: "Approved" },
    { id: "rejected", label: "Rejected" },
    { id: "",        label: "All" },
];

function Stars({ rating }) {
    return (
        <div className="flex items-center gap-0.5">
            {[1,2,3,4,5].map((n) => (
                <Star key={n} size={14}
                      fill={n <= rating ? "var(--color-cta)" : "transparent"}
                      color="var(--color-cta)" strokeWidth={1.5} />
            ))}
        </div>
    );
}

function ReviewRow({ row, onPatch, onDelete }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState({
        customer_name: row.customer_name,
        review_text: row.review_text,
        rating: row.rating,
        admin_note: row.admin_note || "",
    });

    const visibleNow =
        row.permission_confirmed && row.is_verified_real_review && row.show_on_website;

    return (
        <tr className="border-b border-[var(--color-line)] align-top" data-testid={`admin-review-row-${row.id}`}>
            <td className="py-3 pr-3 align-top">
                <div className="text-sm font-semibold" style={{ color: "var(--color-text)" }}>
                    {row.customer_name}
                </div>
                <div className="text-xs mt-0.5" style={{ color: "var(--color-text-dim)" }}>
                    {row.submitted_at?.slice(0, 19)} UTC
                </div>
                {row.email_or_phone ? (
                    <div className="text-xs mt-1" style={{ color: "var(--color-text-muted)" }}>
                        Contact: {row.email_or_phone}
                    </div>
                ) : null}
                {row.service_type || row.city_or_area ? (
                    <div className="text-xs uppercase tracking-wider mt-1" style={{ color: "var(--color-text-dim)" }}>
                        {[row.service_type, row.city_or_area].filter(Boolean).join(" · ")}
                    </div>
                ) : null}
            </td>
            <td className="py-3 pr-3 align-top">
                <Stars rating={row.rating} />
            </td>
            <td className="py-3 pr-3 align-top max-w-xl">
                {editing ? (
                    <div className="space-y-2">
                        <input className="a1-input" value={draft.customer_name}
                               onChange={(e) => setDraft((d) => ({ ...d, customer_name: e.target.value }))} />
                        <textarea className="a1-input" rows={4} value={draft.review_text}
                                  onChange={(e) => setDraft((d) => ({ ...d, review_text: e.target.value }))} />
                        <input className="a1-input" type="number" min="1" max="5" value={draft.rating}
                               onChange={(e) => setDraft((d) => ({ ...d, rating: Number(e.target.value) }))} />
                        <input className="a1-input" placeholder="Admin note (optional)" value={draft.admin_note}
                               onChange={(e) => setDraft((d) => ({ ...d, admin_note: e.target.value }))} />
                        <div className="flex gap-2">
                            <button type="button" className="a1-btn-secondary text-xs px-3 py-1"
                                    onClick={() => { onPatch(row.id, draft); setEditing(false); }}
                                    data-testid={`admin-review-save-${row.id}`}>
                                Save
                            </button>
                            <button type="button" className="a1-btn-secondary text-xs px-3 py-1"
                                    onClick={() => setEditing(false)}>Cancel</button>
                        </div>
                    </div>
                ) : (
                    <>
                        <p className="text-sm m-0" style={{ color: "var(--color-text)" }}>
                            "{row.review_text}"
                        </p>
                        {row.admin_note ? (
                            <p className="text-xs mt-2 italic" style={{ color: "var(--color-text-dim)" }}>
                                Admin note: {row.admin_note}
                            </p>
                        ) : null}
                    </>
                )}
            </td>
            <td className="py-3 pr-3 align-top">
                <div className="flex flex-col gap-1.5 text-xs">
                    <label className="flex items-center gap-2">
                        <input type="checkbox" checked={row.permission_confirmed}
                               onChange={(e) => onPatch(row.id, { permission_confirmed: e.target.checked })}
                               data-testid={`admin-review-toggle-permission-${row.id}`}
                               style={{ accentColor: "var(--color-accent)" }} />
                        Permission confirmed
                    </label>
                    <label className="flex items-center gap-2">
                        <input type="checkbox" checked={row.is_verified_real_review}
                               onChange={(e) => onPatch(row.id, { is_verified_real_review: e.target.checked })}
                               data-testid={`admin-review-toggle-verified-${row.id}`}
                               style={{ accentColor: "var(--color-accent)" }} />
                        Verified real review
                    </label>
                    <label className="flex items-center gap-2">
                        <input type="checkbox" checked={row.show_on_website}
                               onChange={(e) => onPatch(row.id, { show_on_website: e.target.checked })}
                               data-testid={`admin-review-toggle-show-${row.id}`}
                               style={{ accentColor: "var(--color-accent)" }} />
                        Show on website
                    </label>
                    <div className="mt-2 text-[10px] uppercase tracking-wider font-bold"
                         style={{ color: visibleNow ? "var(--color-success)" : "var(--color-warning)" }}
                         data-testid={`admin-review-visibility-${row.id}`}>
                        {visibleNow ? "✓ Live on /reviews" : "Hidden"}
                    </div>
                </div>
            </td>
            <td className="py-3 align-top">
                <div className="flex flex-col gap-1.5">
                    <button type="button" className="a1-btn-secondary text-xs px-3 py-1"
                            onClick={() => setEditing((v) => !v)}
                            data-testid={`admin-review-edit-${row.id}`}>
                        {editing ? "Close" : "Edit"}
                    </button>
                    <button type="button" className="a1-btn-secondary text-xs px-3 py-1"
                            onClick={() => {
                                if (window.confirm(`Delete this review from ${row.customer_name}? This cannot be undone.`)) {
                                    onDelete(row.id);
                                }
                            }}
                            data-testid={`admin-review-delete-${row.id}`}
                            style={{ borderColor: "var(--color-danger)", color: "var(--color-danger)" }}>
                        <Trash2 size={12} className="inline" /> Delete
                    </button>
                </div>
            </td>
        </tr>
    );
}

export default function AdminReviews() {
    const [tab, setTab] = useState("pending");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const q = tab ? `?status_filter=${tab}` : "";
            const r = await api.get(`/admin/reviews${q}`);
            setRows(r.data.reviews || []);
        } finally {
            setLoading(false);
        }
    }, [tab]);

    useEffect(() => { load(); }, [load]);

    const onPatch = async (id, patch) => {
        await api.patch(`/admin/reviews/${id}`, patch);
        await load();
    };
    const onDelete = async (id) => {
        await api.delete(`/admin/reviews/${id}`);
        await load();
    };

    return (
        <div className="space-y-5" data-testid="admin-reviews-page">
            <div className="flex flex-wrap items-baseline gap-4 justify-between">
                <h1 className="text-3xl m-0">Customer Reviews</h1>
                <div className="text-xs" style={{ color: "var(--color-text-dim)" }}>
                    {rows.length} {tab || "all"} review{rows.length === 1 ? "" : "s"}
                </div>
            </div>

            <div
                className="a1-card text-sm"
                data-testid="admin-reviews-schema-note"
                style={{
                    background: "rgba(202, 138, 4, 0.08)",
                    borderColor: "var(--color-warning)",
                }}
            >
                <strong style={{ color: "var(--color-warning)" }}>Admin note:</strong>{" "}
                Review schema should only be enabled after real reviews are verified and approved.
            </div>

            <div className="flex gap-2 flex-wrap" data-testid="admin-reviews-tabs">
                {STATUS_TABS.map((t) => (
                    <button
                        key={t.id || "all"}
                        type="button"
                        onClick={() => setTab(t.id)}
                        className={tab === t.id ? "a1-btn-primary text-xs px-3 py-1" : "a1-btn-secondary text-xs px-3 py-1"}
                        data-testid={`admin-reviews-tab-${t.id || "all"}`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            <div className="a1-card overflow-x-auto p-0">
                <table className="w-full text-sm border-collapse" data-testid="admin-reviews-table">
                    <thead>
                        <tr className="text-left border-b border-[var(--color-line)] uppercase tracking-wider text-xs"
                            style={{ color: "var(--color-text-dim)" }}>
                            <th className="py-3 px-3">Customer</th>
                            <th className="py-3 px-3">Rating</th>
                            <th className="py-3 px-3">Review</th>
                            <th className="py-3 px-3">Moderation</th>
                            <th className="py-3 px-3">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="5" className="p-6 text-center" style={{ color: "var(--color-text-muted)" }}>Loading…</td></tr>
                        ) : rows.length === 0 ? (
                            <tr><td colSpan="5" className="p-6 text-center" style={{ color: "var(--color-text-muted)" }}>
                                No reviews in this tab.
                            </td></tr>
                        ) : (
                            rows.map((r) => <ReviewRow key={r.id} row={r} onPatch={onPatch} onDelete={onDelete} />)
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
